import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import {
  ReceiptIcon, TransportIcon, BookIcon, MealIcon,
  CheckIcon, WalletIcon, MessageSquareIcon, ClockIcon,
  GrowthIcon,
} from "@/components/ui/TriIcons";

const EASE = [0.4, 0, 0.2, 1] as const;
const TEAL = "#7B1416";
const FOREST = "#1C1917";

type Notification = {
  id: string;
  icon: typeof ReceiptIcon;
  title: string;
  body: string;
  time: string;
  color: string;
  delay: number;
};

const notifications: Notification[] = [
  { id: "1", icon: ReceiptIcon, title: "School fees received", body: "UGX 850,000 paid to Kampala International School", time: "now", color: FOREST, delay: 0 },
  { id: "2", icon: TransportIcon, title: "Bus arriving in 4 minutes", body: "Bus 12 is 1.2km away. Sarah will be picked up at 7:42 AM.", time: "2m", color: TEAL, delay: 1.5 },
  { id: "3", icon: BookIcon, title: "Sarah scored 92% in Mathematics", body: "Term 2 midterm results published. Above class average.", time: "1h", color: FOREST, delay: 3 },
  { id: "4", icon: MealIcon, title: "Lunch purchased", body: "UGX 8,000 — Posho, beans, and fruit. Wallet balance: UGX 42,000", time: "3h", color: TEAL, delay: 4.5 },
  { id: "5", icon: CheckIcon, title: "Attendance confirmed", body: "Sarah checked in at 7:48 AM. Gate 2 — Biometric verified.", time: "5h", color: FOREST, delay: 6 },
  { id: "6", icon: MessageSquareIcon, title: "Message from Mr. Okello", body: "Sarah is doing excellently in science. Recommend the advanced lab program.", time: "1d", color: TEAL, delay: 7.5 },
];

export function ParentExperience() {
  const [visibleCount, setVisibleCount] = useState(1);
  const [walletBalance, setWalletBalance] = useState(42000);

  useEffect(() => {
    const id = setInterval(() => {
      setVisibleCount((p) => {
        if (p >= notifications.length) {
          // Reset cycle
          setTimeout(() => setVisibleCount(1), 3000);
          return p;
        }
        return p + 1;
      });
    }, 2200);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (visibleCount === 1) setWalletBalance(42000);
    else if (visibleCount >= 4) setWalletBalance(34000);
  }, [visibleCount]);

  return (
    <div className="grid items-center gap-12 lg:grid-cols-[1fr_1fr] lg:gap-20">
      {/* LEFT — Copy */}
      <div>
        <div className="mb-6 flex items-center gap-4">
          <div className="h-px w-12" style={{ background: FOREST }} />
          <span className="text-[11px] font-medium uppercase tracking-[0.28em]" style={{ color: FOREST }}>
            For Parents
          </span>
        </div>
        <h2 className="font-display text-4xl font-medium leading-[1.1] tracking-tight text-[#1C1917] sm:text-5xl lg:text-[3.5rem] text-balance">
          Every moment of your child's education. In your pocket.
        </h2>
        <p className="mt-6 text-lg leading-[1.7] text-[#1C1917]/80">
          No more backpack notes. No more phone calls to the office. No more wondering
          where the bus is. TriSphere gives parents a single app that connects them to
          every aspect of their child's school day — in real time.
        </p>

        <div className="mt-8 space-y-4">
          {[
            { icon: ReceiptIcon, label: "Pay fees instantly", desc: "Mobile money, card, or wallet — receipts generated automatically" },
            { icon: TransportIcon, label: "Track the bus live", desc: "Know exactly when your child boards and arrives" },
            { icon: BookIcon, label: "See academic progress", desc: "Grades, attendance, and teacher feedback in real time" },
            { icon: WalletIcon, label: "Control spending", desc: "Sub-wallets for meals, books, and transport — with limits" },
          ].map((item, i) => (
            <motion.div
              key={item.label}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.5 }}
              className="flex items-start gap-4"
            >
              <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl" style={{ background: `${FOREST}10` }}>
                <item.icon className="h-5 w-5" accent={FOREST} style={{ color: FOREST }} />
              </div>
              <div>
                <p className="text-base font-medium text-[#1C1917]">{item.label}</p>
                <p className="mt-0.5 text-sm text-[#888]">{item.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* RIGHT — Phone mockup */}
      <div className="flex justify-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: EASE }}
          className="relative"
        >
          {/* Phone frame */}
          <div
            className="relative overflow-hidden rounded-[2.5rem] border-[6px] border-[#1C1917] bg-[#F5EFE4] shadow-float"
            style={{ width: 320, height: 640 }}
          >
            {/* Status bar */}
            <div className="flex items-center justify-between px-6 pt-3 pb-2">
              <span className="text-xs font-semibold text-[#1C1917]">9:41</span>
              <div className="flex items-center gap-1">
                <div className="h-2 w-4 rounded-sm bg-[#1C1917]/30" />
                <div className="h-2 w-3 rounded-sm bg-[#1C1917]/30" />
                <div className="h-3 w-4 rounded-sm bg-[#1C1917]/80" />
              </div>
            </div>

            {/* Notch */}
            <div className="absolute left-1/2 top-0 h-6 w-32 -translate-x-1/2 rounded-b-2xl bg-[#1C1917]" />

            {/* App content */}
            <div className="px-5 pb-5 pt-4">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[#999]">Good morning,</p>
                  <p className="font-display text-lg font-medium text-[#1C1917]">Sarah's Parent</p>
                </div>
                <div className="grid h-9 w-9 place-items-center rounded-full" style={{ background: `${FOREST}10` }}>
                  <WalletIcon className="h-4 w-4" accent={FOREST} style={{ color: FOREST }} />
                </div>
              </div>

              {/* Wallet card */}
              <motion.div
                animate={{ scale: [1, 1.02, 1] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="mt-4 overflow-hidden rounded-2xl p-4"
                style={{ background: `linear-gradient(135deg, ${FOREST}, #1C1917)`, boxShadow: `0 8px 24px ${FOREST}30` }}
              >
                <p className="text-[10px] uppercase tracking-wider text-white/60">Wallet Balance</p>
                <div className="mt-1 flex items-baseline gap-2">
                  <motion.span
                    key={walletBalance}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="font-display text-2xl font-medium text-white"
                  >
                    UGX {walletBalance.toLocaleString()}
                  </motion.span>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <div className="h-1 flex-1 overflow-hidden rounded-full bg-white/10">
                    <motion.div
                      className="h-full rounded-full"
                      style={{ background: TEAL }}
                      animate={{ width: `${(walletBalance / 50000) * 100}%` }}
                      transition={{ duration: 0.5 }}
                    />
                  </div>
                  <span className="text-[10px] text-white/50">{Math.round((walletBalance / 50000) * 100)}%</span>
                </div>
              </motion.div>

              {/* Notifications */}
              <div className="mt-4">
                <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-[#999]">Today's Activity</p>
                <div className="space-y-2">
                  <AnimatePresence>
                    {notifications.slice(0, visibleCount).map((n) => (
                      <motion.div
                        key={n.id}
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.4, ease: EASE }}
                        className="flex items-start gap-3 rounded-xl border border-black/[0.04] bg-white p-3"
                      >
                        <div
                          className="grid h-9 w-9 shrink-0 place-items-center rounded-lg"
                          style={{ background: `${n.color}10` }}
                        >
                          <n.icon className="h-4 w-4" accent={n.color} style={{ color: n.color }} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-semibold text-[#1C1917]">{n.title}</p>
                          <p className="mt-0.5 text-[11px] leading-snug text-[#888]">{n.body}</p>
                        </div>
                        <span className="text-[9px] text-[#bbb]">{n.time}</span>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              </div>
            </div>

            {/* Home indicator */}
            <div className="absolute bottom-1 left-1/2 h-1 w-24 -translate-x-1/2 rounded-full bg-[#1C1917]/20" />
          </div>

          {/* Floating notification bubble */}
          <AnimatePresence>
            {visibleCount > 1 && visibleCount <= notifications.length && (() => {
              const n = notifications[visibleCount - 1];
              const BubbleIcon = n.icon;
              return (
                <motion.div
                  key={`bubble-${visibleCount}`}
                  initial={{ opacity: 0, scale: 0.8, x: 20 }}
                  animate={{ opacity: 1, scale: 1, x: 0 }}
                  exit={{ opacity: 0, scale: 0.8, x: 20 }}
                  transition={{ duration: 0.4, ease: EASE }}
                  className="absolute -right-6 top-32 flex items-center gap-3 rounded-2xl border bg-white p-4 shadow-float"
                  style={{ borderColor: `${n.color}20`, width: 240 }}
                >
                  <div
                    className="grid h-10 w-10 shrink-0 place-items-center rounded-xl"
                    style={{ background: n.color }}
                  >
                    <BubbleIcon
                      className="h-5 w-5"
                      accent="#fff"
                      style={{ color: "#fff" }}
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-[#1C1917]">
                      {n.title}
                    </p>
                    <p className="mt-0.5 text-[10px] text-[#999]">TriSphere · just now</p>
                  </div>
                </motion.div>
              );
            })()}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
}
